import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface LlmProviderSafe {
  id: string;
  tenant_id: string | null;
  label: string;
  preset: string;
  base_url: string;
  api_key_last4: string | null;
  default_model: string;
  model_overrides: Record<string, string>;
  is_active: boolean;
  priority: number;
  health_status: "unknown" | "healthy" | "unhealthy";
  health_checked_at: string | null;
  health_error: string | null;
}

export interface TestResult {
  ok: boolean;
  httpStatus: number;
  latencyMs: number;
  error?: string;
  testToken?: string;
}

interface SaveInput {
  id?: string;
  label: string;
  preset: string;
  baseUrl: string;
  apiKey?: string;
  defaultModel: string;
  modelOverrides?: Record<string, string>;
  testToken?: string;
  skipTest?: boolean;
}

async function call<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("llm-providers", { body });
  if (error) throw new Error(error.message);
  if (data && typeof data === "object" && "error" in data && data.error) {
    throw new Error(String((data as { error: unknown }).error));
  }
  return data as T;
}

export function useLlmProviders() {
  const [providers, setProviders] = useState<LlmProviderSafe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { providers } = await call<{ providers: LlmProviderSafe[] }>({ action: "list" });
      setProviders(providers);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load providers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const testProvider = useCallback(
    (input: { id?: string; baseUrl: string; apiKey?: string; defaultModel: string }) =>
      call<TestResult>({ action: "test", ...input }),
    [],
  );

  const saveProvider = useCallback(
    async (input: SaveInput) => { await call({ action: "save", ...input }); await refresh(); },
    [refresh],
  );

  const setActive = useCallback(
    async (id: string, force = false) => { await call({ action: "set_active", id, force }); await refresh(); },
    [refresh],
  );

  const deleteProvider = useCallback(
    async (id: string) => { await call({ action: "delete", id }); await refresh(); },
    [refresh],
  );

  return { providers, loading, error, refresh, testProvider, saveProvider, setActive, deleteProvider };
}
